import React, { useState } from 'react';
import {
  Container,
  Form,
  Grid,
  Icon,
  Button,
} from 'semantic-ui-react';
import { useNoteContext } from '../context/NoteContext';
import { DiscountAnalysisModal } from '../components/DiscountAnalysisModal';
import { AnalysisSelector } from '../components/AnalysisSelector';
import { FileControls } from '../components/FileControls';
import { Footer } from '../components/Footer';
import {
  formatCurrency,
  formatPercentage,
} from '../utilities/calculations';
import './Analyzer.scss';

const Analyzer: React.FC = () => {
  const { inputs, calculations, updateInput, updateNumberInput, hasRequiredInputs } = useNoteContext();
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [rateDisplay, setRateDisplay] = useState<string>('');
  const [desiredPriceDisplay, setDesiredPriceDisplay] = useState<string>('');

  return (
    <div className="mortgage-analyzer">
      {/* Professional Header */}
      <div className="analyzer-header">
        <Container>
          <div className="header-content">
            <div className="header-branding">
              <div className="logo-container">
                <img
                  src={process.env.PUBLIC_URL + '/logo-transparent.png'}
                  alt="NRC Investments"
                  className="header-logo"
                />
              </div>
              <div className="header-divider" />
              <Icon name="chart line" className="header-icon" />
              <div className="header-text">
                <h1>Mortgage Note Analyzer</h1>
                <p className="subtitle">Comprehensive ROI Analysis & Investment Calculator</p>
              </div>
            </div>
            <div className="header-controls">
              <AnalysisSelector />
              <div className="controls-divider" />
              <FileControls />
            </div>
          </div>
        </Container>
      </div>

      <Container className="main-content">
        {/* Getting Started Guide - Only show when no data */}
        {!hasRequiredInputs && (
          <div className="professional-card" style={{ marginBottom: '2rem', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none' }}>
            <div className="card-body" style={{ padding: '2rem', textAlign: 'center' }}>
              <Icon name="info circle" size="huge" style={{ color: 'white', marginBottom: '1rem' }} />
              <h2 style={{ color: 'white', marginBottom: '1rem' }}>Get Started</h2>
              <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.1rem', marginBottom: '0' }}>
                Enter the <strong>Listed Purchase Price</strong> and <strong>Monthly P&I Payment</strong> below to see your analysis and interactive discount calculator
              </p>
            </div>
          </div>
        )}

        {/* Key Metrics Summary */}
        {hasRequiredInputs && (
          <div className="key-metrics">
            <div className="metrics-grid">
              <div className="metric-card primary">
                <div className="metric-label">Total Investment</div>
                <div className="metric-value">
                  {formatCurrency(calculations.purchasePrice)}
                </div>
                <div className="metric-subtext">Including all fees</div>
              </div>

              <div className="metric-card success">
                <div className="metric-label">Total Return</div>
                <div className="metric-value positive">
                  {formatCurrency(calculations.totalIncome)}
                </div>
                <div className="metric-subtext">
                  {calculations.years.toFixed(1)} years
                </div>
              </div>

              <div className="metric-card warning">
                <div className="metric-label">Annualized ROI</div>
                <div className="metric-value">
                  {formatPercentage(calculations.annualizedROI)}
                </div>
                <div className="metric-subtext">Compound annual growth</div>
              </div>

              <div className="metric-card purple">
                <div className="metric-label">Monthly Income</div>
                <div className="metric-value">
                  {formatCurrency(calculations.monthlyIncome)}
                </div>
                <div className="metric-subtext">Recurring payment</div>
              </div>
            </div>
          </div>
        )}

        {/* Input Forms */}
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={16}>
              <div className="professional-card">
                <div className="card-header">
                  <h3>
                    <Icon name="file alternate outline" />
                    Note Information
                  </h3>
                </div>
                <div className="card-body form-section">
                  <Form>
                    <Form.Input
                      className="full-width-input"
                      label="Property Address / Note Name"
                      placeholder="e.g., 123 Main Street, Anytown, USA"
                      value={inputs.noteName}
                      onChange={(e) => updateInput('noteName', e.target.value)}
                    />
                  </Form>
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column computer={8} tablet={16} mobile={16}>
              <div className="professional-card">
                <div className="card-header">
                  <h3>
                    <Icon name="dollar sign" />
                    Purchase Details & Fees
                  </h3>
                </div>
                <div className="card-body form-section">
                  <Form>
                    <Grid columns={3}>
                      <Grid.Column>
                        <Form.Input
                          label="Listed Purchase Price"
                          placeholder="0.00"
                          type="number"
                          step="0.01"
                          value={inputs.listedPurchasePrice || ''}
                          onChange={(e) =>
                            updateNumberInput('listedPurchasePrice', e.target.value)
                          }
                          icon="dollar"
                          iconPosition="left"
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Form.Input
                          label="Trade Desk Fee"
                          placeholder="3000.00"
                          type="number"
                          step="0.01"
                          value={inputs.tradeDeskFee || ''}
                          onChange={(e) =>
                            updateNumberInput('tradeDeskFee', e.target.value)
                          }
                          icon="dollar"
                          iconPosition="left"
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Form.Input
                          label="Titling Fees"
                          placeholder="180.00"
                          type="number"
                          step="0.01"
                          value={inputs.titlingFees || ''}
                          onChange={(e) =>
                            updateNumberInput('titlingFees', e.target.value)
                          }
                          icon="dollar"
                          iconPosition="left"
                        />
                      </Grid.Column>
                    </Grid>

                    <Grid columns={3}>
                      <Grid.Column>
                        <Form.Input
                          label="Monthly Servicing Fee"
                          placeholder="30.00"
                          type="number"
                          step="0.01"
                          value={inputs.monthlyServicingFee || ''}
                          onChange={(e) =>
                            updateNumberInput('monthlyServicingFee', e.target.value)
                          }
                          icon="dollar"
                          iconPosition="left"
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Form.Input
                          label="Other One-Time Fee"
                          placeholder="400.00"
                          type="number"
                          step="0.01"
                          value={inputs.otherOneTimeFee || ''}
                          onChange={(e) =>
                            updateNumberInput('otherOneTimeFee', e.target.value)
                          }
                          icon="dollar"
                          iconPosition="left"
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Form.Input
                          label="Other Monthly Fee"
                          placeholder="0.00"
                          type="number"
                          step="0.01"
                          value={inputs.otherMonthlyFee || ''}
                          onChange={(e) =>
                            updateNumberInput('otherMonthlyFee', e.target.value)
                          }
                          icon="dollar"
                          iconPosition="left"
                        />
                      </Grid.Column>
                    </Grid>
                  </Form>
                </div>
              </div>
            </Grid.Column>

            <Grid.Column computer={8} tablet={16} mobile={16}>
              <div className="professional-card">
                <div className="card-header">
                  <h3>
                    <Icon name="calculator" />
                    Note Terms
                  </h3>
                </div>
                <div className="card-body form-section">
                  <Form>
                    <Grid columns={3}>
                      <Grid.Column>
                        <Form.Input
                          label="Interest Rate (%)"
                          placeholder="12.75"
                          type="number"
                          step="0.01"
                          value={rateDisplay !== '' ? rateDisplay : (inputs.rate > 0 ? (inputs.rate * 100).toString() : '')}
                          onChange={(e) => {
                            setRateDisplay(e.target.value);
                            const rateValue = (parseFloat(e.target.value) || 0) / 100;
                            updateInput('rate', rateValue);
                          }}
                          onBlur={() => {
                            setRateDisplay('');
                          }}
                          onFocus={() => {
                            if (inputs.rate > 0) {
                              setRateDisplay((inputs.rate * 100).toString());
                            }
                          }}
                          icon="percent"
                          iconPosition="left"
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Form.Input
                          label="Monthly P&I Payment"
                          placeholder="0.00"
                          type="number"
                          step="0.01"
                          value={inputs.principalAndInterest || ''}
                          onChange={(e) =>
                            updateNumberInput('principalAndInterest', e.target.value)
                          }
                          icon="dollar"
                          iconPosition="left"
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Form.Input
                          label="Payments Remaining"
                          placeholder="120"
                          type="number"
                          step="1"
                          value={inputs.paymentsRemaining || ''}
                          onChange={(e) =>
                            updateNumberInput('paymentsRemaining', e.target.value)
                          }
                          icon="calendar alternate outline"
                          iconPosition="left"
                        />
                      </Grid.Column>
                    </Grid>

                    <Grid columns={1}>
                      <Grid.Column>
                        <Form.Input
                          label="Desired Purchase Price (Optional - for discount analysis)"
                          placeholder="0.00"
                          type="number"
                          step="0.01"
                          value={desiredPriceDisplay !== '' ? desiredPriceDisplay : (inputs.desiredPurchasePrice ? Math.round(inputs.desiredPurchasePrice * 100) / 100 : '')}
                          onChange={(e) => {
                            setDesiredPriceDisplay(e.target.value);
                            updateNumberInput('desiredPurchasePrice', e.target.value);
                          }}
                          onBlur={() => {
                            setDesiredPriceDisplay('');
                          }}
                          onFocus={() => {
                            if (inputs.desiredPurchasePrice > 0) {
                              setDesiredPriceDisplay((Math.round(inputs.desiredPurchasePrice * 100) / 100).toString());
                            }
                          }}
                          icon="dollar"
                          iconPosition="left"
                        />
                      </Grid.Column>
                    </Grid>
                  </Form>
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        {/* Analysis Tools Buttons */}
        {hasRequiredInputs && (
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <Button
              primary
              size="large"
              onClick={() => setDiscountModalOpen(true)}
              style={{ flex: '1 1 300px' }}
            >
              <Icon name="percent" />
              Interactive Discount Analysis
            </Button>
          </div>
        )}


        {/* Results Section */}
        {hasRequiredInputs && (
          <div className="results-section">
            {/* Standard Analysis */}
            <Grid stackable>
              <Grid.Row>
                <Grid.Column width={16}>
                  <div className="professional-card">
                    <div className="card-header">
                      <h3>
                        <Icon name="line graph" />
                        Standard Analysis - Based on Listed Price
                      </h3>
                    </div>
                    <div className="card-body">
                      <div className="analysis-grid">
                        <div className="analysis-card">
                          <div className="analysis-header">Purchase Price (with fees)</div>
                          <div className="analysis-value highlight-blue">
                            {formatCurrency(calculations.purchasePrice)}
                          </div>
                        </div>

                        <div className="analysis-card">
                          <div className="analysis-header">Loan Duration</div>
                          <div className="analysis-value">
                            {calculations.years.toFixed(2)} years
                          </div>
                        </div>

                        <div className="analysis-card">
                          <div className="analysis-header">Monthly Income</div>
                          <div className="analysis-value highlight-green">
                            {formatCurrency(calculations.monthlyIncome)}
                          </div>
                        </div>

                        <div className="analysis-card">
                          <div className="analysis-header">Annual Income</div>
                          <div className="analysis-value highlight-green">
                            {formatCurrency(calculations.annualIncome)}
                          </div>
                        </div>

                        <div className="analysis-card">
                          <div className="analysis-header">Total Income</div>
                          <div className="analysis-value highlight-green">
                            {formatCurrency(calculations.totalIncome)}
                          </div>
                        </div>

                        <div className="analysis-card">
                          <div className="analysis-header">Annualized ROI</div>
                          <div className="analysis-value highlight-orange">
                            {formatPercentage(calculations.annualizedROI)}
                          </div>
                        </div>

                        <div className="analysis-card">
                          <div className="analysis-header">Total ROI</div>
                          <div className="analysis-value highlight-orange">
                            {formatPercentage(calculations.roi)}
                          </div>
                        </div>

                        <div className="analysis-card">
                          <div className="analysis-header">IRR (Internal Rate of Return)</div>
                          <div className="analysis-value highlight-orange">
                            {formatPercentage(calculations.irr)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Grid.Column>
              </Grid.Row>

            </Grid>
          </div>
        )}

        {/* Modals */}
        <DiscountAnalysisModal
          open={discountModalOpen}
          onClose={() => setDiscountModalOpen(false)}
        />
      </Container>

      <Footer />
    </div>
  );
};

export default Analyzer;
